import './add-product.scss';
import { v4 as uuidv4 } from 'uuid';
import { useEffect, useRef, useState, Dispatch, SetStateAction } from 'react';
import { Button } from '../../../components/form/button';
import { Typography } from '../../../components/typography/typography';
import Icon from '../../../components/icons/icon';
import classNames from 'classnames';
import { DateTimePicker } from '../../../components/date-picker/date-time-picker';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Product, ProductTrackingType, ProductType } from '../../../clients/quipli-service/product/product';
import { displayMoneyFormat } from '../../../util/util';
import { Location } from '../../../clients/quipli-service/location/location';
import { DateTime, Duration, Interval } from 'luxon';
import { ceilToStep, findNextOpenTimeAfter } from '../../../util/date-time-util';
import {
  CalculateRentalPriceResult,
  CalculateSalePriceResult,
  CustomOrderPriceRequest,
  CustomProductPricingConfig,
  RentalProductPrice,
  SaleProductPrice,
} from '../../../clients/quipli-service/pricing/pricing';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { OrderSection } from '../order-section/order-section';
import { Collapsible, CollapsibleRef } from '../../../components/collapsible/collapsible';
import { OrderType } from '../../../clients/quipli-service/order/order';
import {
  OrderCustomInput,
  OrderInputItem,
  OrderInputRental,
  OrderInputSale,
} from '../../../clients/quipli-service/order/order-calculation';
import { toast } from '../../../util/toasts';
import { EditableField, Field, FieldLabel } from '../../../components/field/field';
import { GroupProductPrice, PriceOption, SearchProductPrice } from './search-product-price';
import { InventoryGantt } from '../../inventory-dashboard/inventory-gantt/inventory-gantt';
import { Spinner } from '../../../components/spinner/spinner';
import { Divider } from '../../../components/divider/divider';
import { groupBy } from 'lodash';
import { useAccountingApiV1 } from '../../../clients/quipli-service/accounting/accounting-api-v1';
import {
  GeneralLedgerAccount,
  GeneralLedgerAccountType,
  ItemType,
} from '../../../clients/quipli-service/accounting/accounting';
import { QuantityInput } from '../../../customer/product-form/quantity-input/quantity-input';
import { useProductApiV1 } from '../../../clients/quipli-service/product/product-api-v1';
import { usePricingApiV1 } from '../../../clients/quipli-service/pricing/pricing-api-v1';
import { TaxCategory } from '../../../clients/quipli-service/tax/tax';
import { useTaxApiV1 } from '../../../clients/quipli-service/tax/tax-api-v1';
import { Radio } from '../../../components/radio/radio';

const DISTANT_FUTURE_DATETIME = DateTime.now().plus({ years: 1000 });

type AddProductType = 'Rental' | 'Sale' | 'Part' | 'Service' | 'Custom';

type AddonOptions = Pick<Product, 'id' | 'name'> & {
  rentalPrice?: CalculateRentalPriceResult;
  salePrice?: CalculateSalePriceResult;
};
type SelectedAddons = Record<number, { active: boolean; addonFor: OrderType }>;

export const AddProduct = ({
  locationId,
  openOnStart = true,
  isCycleBilled,
  cycleLength = Duration.fromObject({ days: 28 }),
  orderId,
  isCashCustomer = false,
  setOrderInputs,
  newOrderInput,
}: {
  locationId?: number;
  isCycleBilled: boolean;
  cycleLength?: Duration;
  openOnStart?: boolean;
  orderId?: number;
  isCashCustomer?: boolean;
  setOrderInputs?: Dispatch<SetStateAction<OrderInputItem[]>>;
  newOrderInput?: (orderInputItem: OrderInputItem) => void;
}) => {
  const { getLocationById, getMultipleConfigurations } = useAPI();
  // States
  const [type, setType] = useState<AddProductType>('Rental');
  const [location, setLocation] = useState<Location>();
  const [cycleBillingEnabled, setCycleBillingEnabled] = useState(false);
  const [customOrderRentalRatesEnabled, setCustomOrderRentalRatesEnabled] = useState(false);
  const [saleSerialTrackingEnabled, setSaleSerialTrackingEnabled] = useState<boolean>(false);
  const [customProductEnabled, setCustomProductEnabled] = useState<boolean>(false);
  const [partsServicesEnabled, setPartsServicesEnabled] = useState<boolean>(false);

  // Vars
  const collapsibleRef = useRef<CollapsibleRef>(null);

  // Callbacks
  const collapse = () => {
    collapsibleRef.current?.toggle(false);
  };

  const onSubmit = ({ input, multipleInputs }: { input: OrderInputItem; multipleInputs: OrderInputItem[] }) => {
    setOrderInputs && setOrderInputs((prev) => [...prev, ...multipleInputs]);
    newOrderInput && newOrderInput(input);
  };

  useEffect(() => {
    if (locationId === undefined) return;

    const getLocation = async () => {
      const l = await getLocationById(locationId);
      setLocation(l);
    };

    getMultipleConfigurations([
      'CONF_CYCLE_BILLING',
      'CONF_CUSTOM_ORDER_RENTAL_RATES',
      'CONF_SALE_SERIAL_TRACKING',
      'CONF_ENABLE_CUSTOM_PRODUCTS',
      'CONF_ENABLE_PARTS',
    ]).then((configs) => {
      setCycleBillingEnabled(configs.find((c) => c.variable === 'CONF_CYCLE_BILLING')?.value === '1');
      setCustomOrderRentalRatesEnabled(
        configs.find((c) => c.variable === 'CONF_CUSTOM_ORDER_RENTAL_RATES')?.value === '1',
      );
      setSaleSerialTrackingEnabled(configs.find((c) => c.variable === 'CONF_SALE_SERIAL_TRACKING')?.value === '1');
      setCustomProductEnabled(configs.find((c) => c.variable === 'CONF_ENABLE_CUSTOM_PRODUCTS')?.value === '1');
      setPartsServicesEnabled(configs.find((c) => c.variable === 'CONF_ENABLE_PARTS')?.value === '1');
    });
    getLocation();
  }, [locationId]);

  useEffect(() => {
    openOnStart && collapsibleRef.current?.toggle(true);
  }, []);

  useEffect(() => {
    isCashCustomer && setType('Sale');
  }, [isCashCustomer]);

  return (
    <div className="order-add-product">
      <OrderSection>
        <Collapsible
          ref={collapsibleRef}
          trigger={(isOpen) => (
            <div className="flex justify-between items-center">
              <Typography type="text-md" weight="medium" className="text-grey-800">
                Add products to order
              </Typography>
              <Icon icon={isOpen ? 'Close' : 'Plus'} stroke="var(--quipli-black-800)" />
            </div>
          )}
        >
          <div className="flex flex-col gap-3">
            <div className="list-tabs">
              <div className="tabs-wrapper">
                <div
                  className={classNames('tab-item', { active: type === 'Rental' })}
                  onClick={() => setType('Rental')}
                  data-cy="rental_tab"
                >
                  <Typography
                    type="text-sm"
                    weight="medium"
                    className={type === 'Rental' ? 'text-quipli-black-800' : 'text-grey-500'}
                  >
                    For rent
                  </Typography>
                  <div className="divider-active"></div>
                </div>
                <div
                  className={classNames('tab-item', { active: type === 'Sale' })}
                  onClick={() => setType('Sale')}
                  data-cy="sale-tab"
                >
                  <Typography
                    type="text-sm"
                    weight="medium"
                    className={type === 'Sale' ? 'text-quipli-black-800' : 'text-grey-500'}
                  >
                    For sale
                  </Typography>
                  <div className="divider-active"></div>
                </div>
                {partsServicesEnabled && (
                  <>
                    <div
                      className={classNames('tab-item', { active: type === 'Part' })}
                      onClick={() => setType('Part')}
                      data-cy="part-tab"
                    >
                      <Typography
                        type="text-sm"
                        weight="medium"
                        className={type === 'Part' ? 'text-quipli-black-800' : 'text-grey-500'}
                      >
                        Parts
                      </Typography>
                      <div className="divider-active"></div>
                    </div>
                    <div
                      className={classNames('tab-item', { active: type === 'Service' })}
                      onClick={() => setType('Service')}
                      data-cy="service-tab"
                    >
                      <Typography
                        type="text-sm"
                        weight="medium"
                        className={type === 'Service' ? 'text-quipli-black-800' : 'text-grey-500'}
                      >
                        Services
                      </Typography>
                      <div className="divider-active"></div>
                    </div>
                  </>
                )}
                {customProductEnabled && (
                  <div
                    className={classNames('tab-item', { active: type === 'Custom' })}
                    onClick={() => setType('Custom')}
                    data-cy="custom-tab"
                  >
                    <Typography
                      type="text-sm"
                      weight="medium"
                      className={type === 'Custom' ? 'text-quipli-black-800' : 'text-grey-500'}
                    >
                      Custom
                    </Typography>
                    <div className="divider-active"></div>
                  </div>
                )}
              </div>
            </div>

            {type !== 'Custom' ? (
              <ProductForm
                orderId={orderId}
                isCycleBilled={isCycleBilled}
                cycleLength={cycleLength}
                location={location!}
                type={OrderType[type === 'Rental' ? 'Rental' : 'Sale']}
                productType={type === 'Rental' || type === 'Sale' ? ProductType.Product : (type as ProductType)}
                cycleBillingEnabled={cycleBillingEnabled}
                customOrderRentalRatesEnabled={customOrderRentalRatesEnabled}
                saleSerialTrackingEnabled={saleSerialTrackingEnabled}
                onSubmit={onSubmit}
                onCollapse={collapse}
              />
            ) : (
              location && (
                <CustomForm
                  onSubmit={({ input }) => {
                    onSubmit({ input, multipleInputs: [input] });
                  }}
                  onCollapse={collapse}
                  location={location}
                  customOrderRentalRatesEnabled={customOrderRentalRatesEnabled}
                />
              )
            )}
          </div>
        </Collapsible>
      </OrderSection>
    </div>
  );
};

const ProductForm = ({
  orderId,
  isCycleBilled,
  cycleLength = Duration.fromObject({ days: 28 }),
  location,
  type,
  productType,
  cycleBillingEnabled,
  customOrderRentalRatesEnabled,
  saleSerialTrackingEnabled,
  onSubmit,
  onCollapse,
}: {
  orderId?: number;
  isCycleBilled: boolean;
  cycleLength?: Duration;
  location: Location;
  type: OrderType.Rental | OrderType.Sale;
  productType: ProductType;
  cycleBillingEnabled: boolean;
  customOrderRentalRatesEnabled: boolean;
  saleSerialTrackingEnabled: boolean;
  onSubmit?: ({ input, multipleInputs }: { input: OrderInputItem; multipleInputs: OrderInputItem[] }) => void;
  onCollapse?: () => void;
}) => {
  const { listPrices } = usePricingApiV1();
  const {
    listProductAddons,
    calculatePriceAsAdmin,
    getAvailableCountByLocations,
    getRentalAvailability,
    confirmAvailability,
    getProduct,
  } = useAPI();

  const [productPrice, setProductPrice] = useState<PriceOption | null>(null);
  const [groupPrice, setGroupPrice] = useState<GroupProductPrice | null>(null);
  const [quantity, setQuantity] = useState<number | undefined>(1);
  const [startDateTime, setStartDateTime] = useState<DateTime | undefined>(
    ceilToStep(DateTime.local(), Duration.fromObject({ minutes: 30 })),
  );
  const [endDateTime, setEndDateTime] = useState<DateTime>();
  // customRentalRatesForCalc should be any modified rental rates that need to be passed in body to
  //      calculation api (either new custom rate OR going back to regular rate)
  const [customRentalRatesForCalc, setCustomRentalRatesForCalc] = useState<{
    [durationStr: string]: CustomOrderPriceRequest | undefined;
  }>({});
  const [calculatedPriceResult, setCalculatedPriceResult] = useState<CalculateRentalPriceResult>();
  const [calculatedSalePrice, setCalculatedSalePrice] = useState<number>();
  const [addons, setAddons] = useState<AddonOptions[]>([]);
  const [selectedAddons, setSelectedAddons] = useState<SelectedAddons>({});
  const [addToOrderClicked, setAddToOrderClicked] = useState(false);
  const [isAvailabilityLoading, setIsAvailabilityLoading] = useState<boolean>(false);
  const [unitsAvailable, setUnitsAvailable] = useState<{ locationId: number; count: number }[]>();
  const [availability, setAvailability] = useState<{ intervals: Interval; color: string }[]>([]);
  const [availableEndDates, setavailableEndDates] = useState<{ intervals: Interval; color: string }[]>([]);
  const [showProductCalendar, setShowProductCalendar] = useState<boolean>(false);
  const [openCalendar, setOpenCalendar] = useState<boolean>(false);
  const [checkAvailability, setCheckAvailability] = useState<boolean>(true);

  const isSubmitDisabled = () => {
    if (!productPrice || !quantity || quantity < 0) return true;

    if (type === OrderType.Rental) {
      if (!startDateTime?.isValid) {
        return true;
      }
      if (!endDateTime) {
        return !cycleBillingEnabled;
      } else {
        if (!endDateTime.isValid) {
          return true;
        } else {
          return endDateTime <= startDateTime;
        }
      }
    }

    return false;
  };

  const clear = () => {
    setCalculatedPriceResult(undefined);
    setCalculatedSalePrice(undefined);
    setQuantity(1);
    setAddons([]);
    setSelectedAddons({});
    setOpenCalendar(false);
    setUnitsAvailable(undefined);
  };

  const submit = async () => {
    if (isSubmitDisabled()) return;

    const commonProps = {
      id: uuidv4(),
      productId: productPrice!.object.productId,
      quantity: quantity,
    };

    const product: OrderInputItem =
      type === OrderType.Rental
        ? ({
            ...commonProps,
            type: OrderType.Rental,
            start: startDateTime!,
            end: endDateTime,
            assignedUnits: [],
            customOrderRentalRates: Object.values(customRentalRatesForCalc).filter(
              (r) => !!r,
            ) as CustomOrderPriceRequest[],
          } as OrderInputRental)
        : ({
            ...commonProps,
            type: OrderType.Sale,
            isCustom: false,
          } as OrderInputSale);

    const newProds: OrderInputItem[] = [];
    try {
      await checkProductAvailability(product);
      newProds.push(product);
    } catch (error: any) {
      // if main product fails we don't want to add addons
      toast(error.message, 'error');
      return;
    }

    // Now we add addons
    const addonsToAdd = Object.entries(selectedAddons)
      .filter((addon) => addon[1].active)
      .map(
        ([id, addon]) =>
          ({
            id: uuidv4(),
            type: addon.addonFor,
            productId: parseInt(id),
            quantity: 1,
            ...(addon.addonFor === OrderType.Rental
              ? {
                  start: startDateTime!,
                  end: endDateTime!,
                  assignedUnits: [],
                }
              : {
                  isCustom: false,
                }),
          } as OrderInputRental | OrderInputSale),
      );

    if (addonsToAdd.length) {
      for (const addon of addonsToAdd) {
        try {
          await checkProductAvailability(addon);
          newProds.push(addon);
        } catch (error: any) {
          toast(error.message, 'error');
        }
      }
    }

    onSubmit && onSubmit({ input: product, multipleInputs: newProds });
    toast('Product added to order.');

    clear();
    setProductPrice(null);
    setAddToOrderClicked(true);
  };

  const checkProductAvailability = async (product: OrderInputRental | OrderInputSale) => {
    await confirmAvailability({ ...product, locationId: location!.id });
  };

  const handleOnChangePrice = async (op: PriceOption | null) => {
    if (!location) return;

    setProductPrice(op);
    setCustomRentalRatesForCalc({});

    // Option removed
    if (!op) {
      clear();
      return;
    }

    let currentStart: DateTime;
    let currentEnd: DateTime;

    // Calculate sale price
    if (op.object.type === OrderType.Sale) {
      setStartDateTime(undefined);
      setEndDateTime(undefined);

      calculatePriceAsAdmin({ productId: op.object.productId, type: OrderType.Sale, locationId: location?.id }).then(
        (res) => {
          setCalculatedSalePrice((res as CalculateSalePriceResult).totalPrice);
        },
      );

      handleOnChangeAddons(op);
    }

    // Set rental dates for rental products
    else {
      if (startDateTime) {
        currentStart = startDateTime;
      } else {
        currentStart = ceilToStep(
          findNextOpenTimeAfter(
            location.hoursOfOperation,
            location.disabledDates,
            'rentalStart',
            DateTime.local({ zone: location.timezone }).plus(location.minimumLeadTime).plus(location.defaultLeadTime),
            [],
          ),
          Duration.fromObject({ minutes: 30 }),
        );
      }

      const rentalPrice = op.object as RentalProductPrice;
      if (endDateTime && addToOrderClicked) {
        currentEnd = endDateTime;
        setEndDateTime(endDateTime);
      } else {
        currentEnd = currentStart.plus({ [rentalPrice.durationUnit]: rentalPrice.durationValue });
        setEndDateTime(currentEnd);
      }
      setStartDateTime(currentStart);
    }
  };

  const filterAddonsCallback = (
    productFor: AddProductType,
    allowRent: boolean,
    allowSale: boolean,
    rentUnits: RentalProductPrice[],
    saleUnits: SaleProductPrice[],
  ) => {
    if (
      productFor === OrderType.Rental &&
      allowRent &&
      rentUnits.some(
        ({ locationIds }) => !locationIds || locationIds.length === 0 || locationIds.includes(location!.id),
      )
    ) {
      return OrderType.Rental;
    }

    if (
      (productFor === OrderType.Sale || productFor === OrderType.Rental) &&
      allowSale &&
      saleUnits.some(
        ({ locationIds }) => !locationIds || locationIds.length === 0 || locationIds.includes(location!.id),
      )
    ) {
      return OrderType.Sale;
    }

    return undefined;
  };

  const handleOnChangeAddons = (op: PriceOption) => {
    if (!location) return;

    // Get addons
    listProductAddons(op.object.productId, location.id).then(async (prods) => {
      const _selected: SelectedAddons = {};
      const prices = await listPrices(
        prods.map((p) => p.id),
        location.id,
        undefined,
        undefined,
        null,
      ).then((rps) => groupBy(rps, 'productId'));
      const _addons = await Promise.all(
        prods
          .filter(
            (p) =>
              filterAddonsCallback(
                type,
                p.allowRent,
                p.allowSale,
                (prices[p.id] ?? []).filter((price) => price.type === OrderType.Rental) as RentalProductPrice[],
                (prices[p.id] ?? []).filter((price) => price.type === OrderType.Sale) as SaleProductPrice[],
              ) !== undefined,
          )
          .map(async (p) => {
            _selected[p.id] = {
              active: selectedAddons[p.id]?.active ?? false,
              addonFor: filterAddonsCallback(
                type,
                p.allowRent,
                p.allowSale,
                (prices[p.id] ?? []).filter((price) => price.type === OrderType.Rental) as RentalProductPrice[],
                (prices[p.id] ?? []).filter((price) => price.type === OrderType.Sale) as SaleProductPrice[],
              )!,
            };

            if (_selected[p.id].addonFor === OrderType.Rental) {
              const maxEndDate = startDateTime!.plus({ days: 28 });
              const rentalPrice = (await calculatePriceAsAdmin({
                productId: p.id,
                type: OrderType.Rental,
                start: startDateTime!,
                end: endDateTime ? (isCycleBilled ? DateTime.min(endDateTime, maxEndDate) : endDateTime) : maxEndDate,
                locationId: location.id,
              })) as CalculateRentalPriceResult;

              return { id: p.id, name: p.name, rentalPrice };
            } else {
              const salePrice = (await calculatePriceAsAdmin({
                productId: p.id,
                type: OrderType.Sale,
                locationId: location.id,
              })) as CalculateSalePriceResult;

              return { id: p.id, name: p.name, salePrice };
            }
          }),
      );

      setSelectedAddons(_selected);
      setAddons(_addons);
    });
  };

  useEffect(() => {
    clear();
    setProductPrice(null);
  }, [type, productType]);

  useEffect(() => {
    if (!endDateTime && !cycleBillingEnabled) {
      return;
    }

    // If the end date is before the start date, clear the calculated price
    if (startDateTime && endDateTime && startDateTime >= endDateTime) {
      setCalculatedPriceResult(undefined);
      return;
    }

    if (!productPrice || !startDateTime) {
      return;
    }

    const maxEndDate = startDateTime.plus(cycleLength);

    calculatePriceAsAdmin({
      productId: productPrice.object.productId,
      type: OrderType.Rental,
      start: startDateTime,
      end: endDateTime ? (isCycleBilled ? DateTime.min(endDateTime, maxEndDate) : endDateTime) : maxEndDate,
      locationId: location?.id,
      orderId,
      customOrderRentalRates: Object.values(customRentalRatesForCalc).filter((r) => !!r) as CustomOrderPriceRequest[],
    }).then((res) => {
      const result = res as CalculateRentalPriceResult;
      setCalculatedPriceResult(result);
    });

    handleOnChangeAddons(productPrice);
  }, [productPrice?.object.productId, startDateTime, endDateTime, isCycleBilled, customRentalRatesForCalc]);

  useEffect(() => {
    const manageAvailability = async () => {
      let maxEndDate, finalEndDate;

      // Want to fetch the tracking type so we can determine if we need to do availability checks
      const product = await getProduct(productPrice!.object.productId);
      const trackingType = product.trackingType;

      const untracked = trackingType == ProductTrackingType.NonInventory;

      setCheckAvailability(!untracked);

      if (productPrice?.object.type === OrderType.Rental && !untracked) {
        if (!endDateTime && !cycleBillingEnabled) {
          unitsAvailable !== undefined && setUnitsAvailable(undefined);
          setShowProductCalendar(false);
          return;
        }

        if (!productPrice || !startDateTime || !location) {
          unitsAvailable !== undefined && setUnitsAvailable(undefined);
          setShowProductCalendar(false);
          return;
        }

        maxEndDate = startDateTime.plus(cycleLength);
        finalEndDate = endDateTime ? (isCycleBilled ? DateTime.min(endDateTime, maxEndDate) : endDateTime) : maxEndDate;
      }

      if (productPrice && !untracked) {
        setIsAvailabilityLoading(true);
        getAvailableCountByLocations(
          productPrice.object.productId,
          productPrice.object.type,
          startDateTime ?? DateTime.now(),
          finalEndDate ?? DateTime.now(),
        ).then((res) => {
          setIsAvailabilityLoading(false);
          setUnitsAvailable(res);
        });
        setShowProductCalendar(true);
      } else {
        setUnitsAvailable(undefined);
      }
    };

    if (productPrice?.object.productId) {
      manageAvailability();
    }
  }, [productPrice?.object.productId, location, startDateTime, endDateTime]);

  useEffect(() => {
    if (type !== OrderType.Rental || !productPrice || !location || quantity === undefined || quantity < 0) {
      return;
    }

    getRentalAvailability(productPrice.object.productId, location.id, quantity).then((res) =>
      setAvailability(
        res
          .filter((a) => a !== undefined)
          .map((a) => {
            return {
              intervals: Interval.fromDateTimes(a.start!, a.end || DISTANT_FUTURE_DATETIME),
              color: 'var(--success-200)',
            };
          }),
      ),
    );
  }, [productPrice?.object.productId, location, quantity, type]);

  useEffect(() => {
    if (startDateTime?.isValid) {
      if (availability.length && location) {
        const currentAvailabilityInterval = availability
          .map((m) => m.intervals)
          .reverse()
          .find((av) => {
            const startsBefore = av.start === undefined || av.start.diff(startDateTime).milliseconds < 0;
            const endsAfter = av.end === undefined || av.end.diff(startDateTime).milliseconds > 0;
            return startsBefore && endsAfter;
          });

        const availabilityEnd = currentAvailabilityInterval?.end?.plus(1);
        if (availabilityEnd !== undefined) {
          setavailableEndDates([
            {
              intervals: Interval.fromDateTimes(startDateTime, availabilityEnd),
              color: 'var(--success-200)',
            },
          ]);
        }
      }
    } else {
      setavailableEndDates([]);
    }
  }, [startDateTime, availability, location]);

  return (
    <>
      <div className="flex flex-col gap-[4px]">
        <FieldLabel label="Product" isRequired dataCy="product_field" />
        <SearchProductPrice
          location={location}
          orderId={orderId}
          type={OrderType[type]}
          value={productPrice || undefined}
          onSelect={handleOnChangePrice}
          onGroupSelect={setGroupPrice}
          productType={productType}
        />
      </div>

      {productType !== ProductType.Service && (
        <Field
          label="Quantity"
          isRequired
          type="number"
          value={productPrice === null ? null : 1}
          disabled={productPrice === null}
          onChange={(qty?: number) => setQuantity(qty)}
          dataCy="product_quantity"
        />
      )}

      {type === OrderType.Sale && checkAvailability && productPrice?.object.productId && groupPrice && (
        <AvailabilityText
          availability={
            groupPrice.trackingType === ProductTrackingType.SerialUnits && saleSerialTrackingEnabled
              ? unitsAvailable?.find((l) => l.locationId === location?.id)?.count ?? 0
              : groupPrice.stock?.find((s) => s.locationId === location?.id)?.saleStock || groupPrice.saleStock || 0
          }
          selectedQty={quantity}
        />
      )}

      {type === OrderType.Rental && (
        <>
          <div className="flex flex-col md:flex-row gap-y-[16px] gap-x-[24px]">
            <div className="flex-grow">
              <DateTimePicker
                label="Rent from"
                value={startDateTime}
                onChange={setStartDateTime}
                timezone={location?.timezone}
                highlightedIntervals={availability}
                usePortal
                data-cy="product_start_date"
              />
            </div>

            <div className="flex-grow">
              <DateTimePicker
                label="Rent to"
                value={endDateTime}
                onChange={setEndDateTime}
                timezone={location?.timezone}
                disabledIntervals={[
                  Interval.before(startDateTime?.plus({ minutes: 30 }) || DateTime.now(), { years: 100 }),
                ]}
                highlightedIntervals={availableEndDates}
                usePortal
                clearable={isCycleBilled}
                data-cy="product_end_date"
              />
            </div>
          </div>

          {location && productPrice?.object.productId && groupPrice && (
            <>
              <div className="flex items-start justify-between gap-2">
                {isAvailabilityLoading ? (
                  <div className="flex justify-center w-full">
                    <Spinner />
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col gap-1">
                      {unitsAvailable && (
                        <>
                          <AvailabilityText
                            availability={unitsAvailable.find((u) => u.locationId === location.id)?.count || 0}
                            selectedQty={quantity}
                            complementMessage={` at ${location.name}`}
                          />

                          {checkAvailability &&
                            (groupPrice.trackingType === ProductTrackingType.SerialUnits ||
                              (groupPrice.stock && groupPrice.stock.length > 0)) &&
                            unitsAvailable.length > 1 && (
                              <AvailabilityText
                                availability={unitsAvailable
                                  .filter((u) => u.locationId !== location.id)
                                  .reduce((acc, u) => acc + u.count, 0)}
                                selectedQty={quantity}
                                complementMessage={' at other locations'}
                              />
                            )}
                        </>
                      )}
                    </div>

                    {showProductCalendar && startDateTime?.isValid && (
                      <Button className="button-secondary" onClick={() => setOpenCalendar((prev) => !prev)}>
                        {!openCalendar ? 'Open' : 'Close'} Calendar
                      </Button>
                    )}
                  </>
                )}
              </div>

              {showProductCalendar && openCalendar && !isAvailabilityLoading && startDateTime?.isValid && (
                <ProductInventoryCalendar
                  productId={productPrice?.object.productId}
                  startDateTime={startDateTime!}
                  endDateTime={endDateTime}
                  isCycleBilled={isCycleBilled}
                  cycleLength={cycleLength}
                />
              )}
            </>
          )}
        </>
      )}
      <div className="flex flex-col gap-[4px]">
        <FieldLabel label="Pricing Breakdown" />
        <>
          {productPrice?.object.type === OrderType.Rental && customOrderRentalRatesEnabled && (
            <div className={'flex-[0_1_calc(100%-20px)] show-prices'}>
              <table className={'invoice-table min-w-full'}>
                <thead>
                  <tr data-cy="rental_prices_table_headers">
                    {groupPrice?.rentalPrices.map((price: RentalProductPrice) => {
                      return (
                        <th key={`rental-price-head-${productPrice.object.id}-${price.id}`} className="text-left pl-2">
                          <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
                            {price.durationLabel ?? 'Price'}
                          </Typography>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  <tr data-cy="rental_prices_table_prices_row">
                    {groupPrice?.rentalPrices.map((price: RentalProductPrice) => {
                      return (
                        <td key={`rental-price-body-${productPrice.object.id}-${price.id}`} className="p-2">
                          <EditableField
                            type="number"
                            value={customRentalRatesForCalc[`${price.durationValue}-${price.durationUnit}`] ?? price}
                            formattedValue={displayMoneyFormat(
                              Math.round(
                                (customRentalRatesForCalc[`${price.durationValue}-${price.durationUnit}`]?.price ??
                                  price.price) / 100,
                              ),
                              location?.currency,
                            )}
                            canReset={
                              !!customRentalRatesForCalc[`${price.durationValue}-${price.durationUnit}`] &&
                              customRentalRatesForCalc[`${price.durationValue}-${price.durationUnit}`]?.price !==
                                price.price
                            }
                            resetPosition="right"
                            onReset={() => {
                              setCustomRentalRatesForCalc({
                                ...customRentalRatesForCalc,
                                [`${price.durationValue}-${price.durationUnit}`]: undefined,
                              });
                            }}
                            onChange={(v) => {
                              setCustomRentalRatesForCalc({
                                ...customRentalRatesForCalc,
                                [`${price.durationValue}-${price.durationUnit}`]: {
                                  ...price,
                                  price: v * 100,
                                  action:
                                    v * 100 === price.originalPrice?.price
                                      ? 'remove'
                                      : price.originalPrice?.price
                                      ? 'update'
                                      : 'add',
                                  idToChange:
                                    v * 100 === price.originalPrice?.price
                                      ? price.id
                                      : price.originalPrice?.price
                                      ? price.id
                                      : undefined,
                                },
                              });
                            }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
        <div className="flex">
          <div className="flex-[calc(50%_-_(24px_/_2))] flex-grow-0 rounded-[4px] bg-grey-50 px-[4px] py-[6px]">
            {!calculatedPriceResult && !calculatedSalePrice && <span className="invisible">b</span>}

            {!!calculatedPriceResult && (
              <>
                {calculatedPriceResult.componentPrices &&
                  calculatedPriceResult.componentPrices.map((componentPrice, index) => {
                    const productSelected = groupPrice;
                    const og = productSelected?.rentalPrices.find((rp) => {
                      return (
                        rp.durationValue == componentPrice.unitPrice.durationValue &&
                        rp.durationUnit == componentPrice.unitPrice.durationUnit &&
                        (!rp.orderId || rp.originalPrice?.id)
                      );
                    });
                    const originalPrice =
                      productSelected &&
                      (!!customRentalRatesForCalc[
                        `${componentPrice.unitPrice.durationValue}-${componentPrice.unitPrice.durationUnit}`
                      ]?.price ||
                        !!componentPrice.unitPrice.orderId)
                        ? og?.originalPrice?.price ?? og?.price
                        : undefined;
                    return (
                      <Typography key={`component-price-${index}`} type="text-sm" className="text-grey-500">
                        {Math.round(componentPrice.consumedUnits * 1000) / 1000} x{' '}
                        {
                          <s className={classNames('text-xs pr-1', { hidden: !componentPrice.unitPrice.orderId })}>
                            {displayMoneyFormat(
                              componentPrice.unitPrice.orderId && originalPrice ? originalPrice / 100 : 1,
                              location?.currency,
                            )}
                          </s>
                        }
                        {displayMoneyFormat(componentPrice.unitPrice.price / 100, location?.currency)}{' '}
                        {`Per ${componentPrice.unitPrice.durationValue} ${componentPrice.unitPrice.durationUnit}${
                          componentPrice.unitPrice.durationValue > 1 ? 's' : ''
                        }`}
                        {index === calculatedPriceResult.componentPrices.length - 1 && (
                          <>{` = ${displayMoneyFormat(calculatedPriceResult.totalPrice / 100, location?.currency)}`}</>
                        )}
                      </Typography>
                    );
                  })}
              </>
            )}

            {!!calculatedSalePrice && (
              <Typography type="text-sm">
                {`${displayMoneyFormat(calculatedSalePrice / 100, location?.currency)}`}
              </Typography>
            )}
          </div>
        </div>
      </div>
      {addons.length > 0 && (
        <div className="flex flex-col gap-[4px]">
          <Typography type="text-md" weight="medium" className="text-grey-700">
            Addons
          </Typography>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-cy="addons_wrapper">
            {addons.map((addon, index) => (
              <Checkbox
                key={`addon-${index}`}
                type="white"
                isChecked={selectedAddons[addon.id].active}
                label={
                  <span className="!text-grey-700 flex items-center gap-[6px] flex-wrap">
                    <span>{addon.name}</span>
                    <span className="font-medium">
                      {selectedAddons[addon.id].addonFor === OrderType.Sale && type === OrderType.Rental
                        ? 'Sale - '
                        : null}
                      {displayMoneyFormat(
                        ((selectedAddons[addon.id].addonFor === OrderType.Rental
                          ? addon.rentalPrice?.totalPrice
                          : addon.salePrice?.totalPrice) || 0) / 100,
                        location?.currency,
                      )}
                    </span>
                  </span>
                }
                onChange={(checked) =>
                  setSelectedAddons({
                    ...selectedAddons,
                    [addon.id]: { active: checked, addonFor: selectedAddons[addon.id].addonFor },
                  })
                }
              />
            ))}
          </div>
        </div>
      )}
      <div className="button-group">
        <Button className="button-secondary" onClick={onCollapse} data-cy="cancel_add_product_to_order">
          Cancel
        </Button>
        <Button
          className="button-primary"
          disabled={isSubmitDisabled()}
          onClick={submit}
          data-cy="add_product_to_order"
        >
          Add to order
        </Button>
      </div>
    </>
  );
};

const CustomForm = ({
  onSubmit,
  onCollapse,
  location,
  customOrderRentalRatesEnabled,
}: {
  onSubmit?: ({ input }: { input: OrderInputItem }) => void;
  onCollapse?: () => void;
  location: Location;
  customOrderRentalRatesEnabled: boolean;
}) => {
  const { getCustomProduct } = useProductApiV1();
  const { listCustomProductPricingConfigs } = usePricingApiV1();
  const { listAccounts, getItemGeneralLedgerAccount } = useAccountingApiV1();
  const { searchTaxCategories } = useTaxApiV1();
  const { calculatePriceAsAdmin } = useAPI();

  const [customProduct, setCustomProduct] = useState<Product>();
  const [customProductPricingConfigs, setCustomProductPricingConfigs] = useState<CustomProductPricingConfig[]>([]);
  const [customRentalProductPrices, setCustomRentalProductPrices] = useState<RentalProductPrice[]>([]);
  const [orderType, setOrderType] = useState<OrderType>(OrderType.Sale);
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [accounts, setAccounts] = useState<GeneralLedgerAccount[]>();
  const [generalLedgerAccount, setGeneralLedgerAccount] = useState<GeneralLedgerAccount | undefined>();
  const [taxCategories, setTaxCategories] = useState<TaxCategory[]>();
  const [taxCategory, setTaxCategory] = useState<TaxCategory | undefined>();
  const [price, setPrice] = useState<number | undefined>();
  const [qty, setQty] = useState<number>(1);
  const [taxable, setTaxable] = useState<boolean>(true);
  const [startDateTime, setStartDateTime] = useState<DateTime | undefined>();
  const [endDateTime, setEndDateTime] = useState<DateTime | undefined>();
  const [customRentalRatesForCalc, setCustomRentalRatesForCalc] = useState<{
    [durationStr: string]: CustomOrderPriceRequest | undefined;
  }>({});
  const [calculatedPriceResult, setCalculatedPriceResult] = useState<CalculateRentalPriceResult>();
  const [equipmentOwner, setEquipmentOwner] = useState<string>();

  const isDisabled =
    !customProduct ||
    !name ||
    price === undefined ||
    !qty ||
    (orderType === OrderType.Rental &&
      (!startDateTime ||
        !endDateTime ||
        !calculatedPriceResult ||
        !Object.values(customRentalRatesForCalc).every((v) => v !== undefined)));

  const submit = () => {
    if (isDisabled) return;

    let input: OrderCustomInput;
    switch (orderType) {
      case OrderType.Sale:
        input = {
          id: uuidv4(),
          type: OrderType.Sale,
          productId: customProduct!.id,
          isCustom: true,
          quantity: qty,
          name,
          description,
          customPrice: Math.round(price * 100),
          taxable,
          generalLedgerAccountId: generalLedgerAccount?.id,
          taxCategoryId: taxCategory?.id,
        };
        break;
      case OrderType.Rental:
        input = {
          id: uuidv4(),
          type: OrderType.Rental,
          productId: customProduct!.id,
          isCustom: true,
          quantity: qty,
          name,
          description,
          customPrice: Math.round(price * 100),
          taxable,
          generalLedgerAccountId: generalLedgerAccount?.id,
          taxCategoryId: taxCategory?.id,
          start: startDateTime!,
          end: endDateTime!,
          customOrderRentalRates: Object.values(customRentalRatesForCalc).filter(
            (r) => !!r,
          ) as CustomOrderPriceRequest[],
          equipmentOwner,
        };
        break;
    }

    onSubmit && onSubmit({ input });
    toast('Custom product added to order.');

    clear();
  };

  function onTaxCategoryChange(selected?: { label: string; value: string }) {
    if (selected) {
      setTaxCategory(taxCategories?.find((a) => a.id === selected.value));
    } else {
      setTaxCategory(undefined);
    }
  }

  const clear = () => {
    setName('');
    setDescription('');
    setQty(1);
    setGeneralLedgerAccount(undefined);
    setCustomRentalRatesForCalc({});
    setPrice(undefined);
    setTaxable(true);
  };

  const buildCustomRentalProductPrices = (customProductPricingConfigs: CustomProductPricingConfig[]) => {
    const basePriceOption = {
      id: uuidv4(),
      productId: customProduct?.id ?? 0,
      price: price ?? 0,
      type: OrderType.Rental,
      featured: false,
      createdAt: DateTime.now(),
      locationIds: undefined,
    };

    const customPriceOptions: RentalProductPrice[] = [];

    const addPriceOption = (config: CustomProductPricingConfig, durationMinutes: number, durationLabel: string) => {
      customPriceOptions.push({
        ...basePriceOption,
        type: OrderType.Rental,
        durationValue: config.durationValue,
        durationUnit: config.durationUnit,
        durationMinutes: durationMinutes * config.durationValue,
        durationLabel: `Per ${durationLabel}${config.durationValue > 1 ? 's' : ''}`,
      } as RentalProductPrice);
    };

    // We want the pricing options to be sorted in ascending order by unit an value,
    // so we have to manually add them in the correct order

    // Add hourly pricing
    customProductPricingConfigs
      .filter((c) => c.durationUnit === 'hour')
      .sort((c) => c.durationValue)
      .map((config) => {
        addPriceOption(config, 60, 'Hour');
      });

    // Add daily pricing
    customProductPricingConfigs
      .filter((c) => c.durationUnit === 'day')
      .sort((c) => c.durationValue)
      .map((config) => {
        addPriceOption(config, 1440, 'Day');
      });

    // Add weekly pricing
    customProductPricingConfigs
      .filter((c) => c.durationUnit === 'week')
      .sort((c) => c.durationValue)
      .map((config) => {
        addPriceOption(config, 10080, 'Week');
      });

    // Add monthly pricing
    customProductPricingConfigs
      .filter((c) => c.durationUnit === 'month')
      .sort((c) => c.durationValue)
      .map((config) => {
        addPriceOption(config, 43200, 'Month');
      });

    return customPriceOptions;
  };

  useEffect(() => {
    Promise.all([
      getCustomProduct().then(setCustomProduct),
      listCustomProductPricingConfigs().then(setCustomProductPricingConfigs),
      searchTaxCategories({}).then(setTaxCategories),
      listAccounts(false, GeneralLedgerAccountType.Income).then((res) => {
        setAccounts(res);
        getItemGeneralLedgerAccount(ItemType.CustomProduct).then((item) =>
          setGeneralLedgerAccount(res.find((a) => a.id === item?.generalLedgerAccountId)),
        );
      }),
    ]);
  }, []);

  useEffect(() => {
    setCustomRentalProductPrices(buildCustomRentalProductPrices(customProductPricingConfigs));
  }, [customProductPricingConfigs]);

  useEffect(() => {
    if (startDateTime && endDateTime && Object.keys(customRentalRatesForCalc).length && customProduct) {
      calculatePriceAsAdmin({
        productId: customProduct?.id,
        type: OrderType.Rental,
        start: startDateTime,
        end: endDateTime,
        customOrderRentalRates: Object.values(customRentalRatesForCalc).filter((r) => !!r) as CustomOrderPriceRequest[],
      }).then((res) => {
        setCalculatedPriceResult(res as CalculateRentalPriceResult);
        setPrice((res as CalculateRentalPriceResult).totalPrice / 100);
      });
    }
  }, [startDateTime, endDateTime, customRentalRatesForCalc]);

  return (
    <>
      <div className="flex gap-4">
        <Radio label="Sale" isChecked={orderType === OrderType.Sale} onChange={() => setOrderType(OrderType.Sale)} />
        <Radio
          label="Rental"
          isChecked={orderType === OrderType.Rental}
          onChange={() => setOrderType(OrderType.Rental)}
        />
      </div>
      <Field label="Name" isRequired type="text" value={name} onChange={(v) => setName(v)} dataCy="custom_field_name" />

      <Field
        label="Description"
        type="text"
        value={description}
        onChange={(v) => setDescription(v)}
        dataCy="custom_field_description"
      />

      {orderType === OrderType.Rental && (
        <>
          <Field
            label="Equipment Owner"
            type="text"
            value={equipmentOwner}
            onChange={(v) => setEquipmentOwner(v)}
            dataCy="custom_field_equipment_owner"
          />

          <div className="flex flex-col md:flex-row gap-y-[16px] gap-x-[24px]">
            <div className="flex-grow">
              <DateTimePicker
                label="Rent from"
                value={startDateTime}
                onChange={setStartDateTime}
                timezone={location?.timezone}
                usePortal
                data-cy="product_start_date"
              />
            </div>

            <div className="flex-grow">
              <DateTimePicker
                label="Rent to"
                value={endDateTime}
                onChange={setEndDateTime}
                timezone={location?.timezone}
                disabledIntervals={[
                  Interval.before(startDateTime?.plus({ minutes: 30 }) || DateTime.now(), { years: 100 }),
                ]}
                usePortal
                clearable={false}
                data-cy="product_end_date"
              />
            </div>
          </div>
        </>
      )}

      {orderType === OrderType.Rental && customOrderRentalRatesEnabled && (
        <table className={'invoice-table min-w-full'}>
          <thead>
            <tr data-cy="rental_prices_table_headers">
              {customRentalProductPrices.map((price: RentalProductPrice) => {
                return (
                  <th className="text-left">
                    {`Per ${price.durationValue > 1 ? `${price.durationValue} ` : ''}${price.durationUnit}${
                      price.durationValue > 1 ? 's' : ''
                    }`}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <tr data-cy="rental_prices_table_prices_row">
              {customRentalProductPrices.map((price: RentalProductPrice) => {
                return (
                  <td key={`rental-price-body-${price.durationValue}-${price.durationUnit}`} className="p-2">
                    <EditableField
                      type="number"
                      value={
                        customRentalRatesForCalc[`${price.durationValue}-${price.durationUnit}`]?.price
                          ? customRentalRatesForCalc[`${price.durationValue}-${price.durationUnit}`]!.price / 100
                          : undefined
                      }
                      formattedValue={
                        customRentalRatesForCalc[`${price.durationValue}-${price.durationUnit}`]
                          ? displayMoneyFormat(
                              (customRentalRatesForCalc[`${price.durationValue}-${price.durationUnit}`]?.price ??
                                price.price) / 100,

                              location?.currency,
                            )
                          : '--'
                      }
                      canReset={
                        !!customRentalRatesForCalc[`${price.durationValue}-${price.durationUnit}`] &&
                        customRentalRatesForCalc[`${price.durationValue}-${price.durationUnit}`]?.price !== price.price
                      }
                      resetPosition="right"
                      onReset={() => {
                        setCustomRentalRatesForCalc({
                          ...customRentalRatesForCalc,
                          [`${price.durationValue}-${price.durationUnit}`]: undefined,
                        });
                      }}
                      onChange={(v) => {
                        setCustomRentalRatesForCalc({
                          ...customRentalRatesForCalc,
                          [`${price.durationValue}-${price.durationUnit}`]: {
                            ...price,
                            price: v * 100,
                            action:
                              v * 100 === price.originalPrice?.price
                                ? 'remove'
                                : price.originalPrice?.price
                                ? 'update'
                                : 'add',
                          },
                        });
                      }}
                    />
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      )}

      {!!calculatedPriceResult && (
        <div className="flex-[calc(50%_-_(24px_/_2))] flex-grow-0 rounded-[4px] bg-grey-50 px-[4px] py-[6px]">
          {calculatedPriceResult.componentPrices &&
            calculatedPriceResult.componentPrices.map((componentPrice, index) => {
              return (
                <Typography key={`component-price-${index}`} type="text-sm" className="text-grey-500">
                  {Math.round(componentPrice.consumedUnits * 1000) / 1000} x{' '}
                  {displayMoneyFormat(componentPrice.unitPrice.price / 100, location?.currency)}{' '}
                  {`Per ${componentPrice.unitPrice.durationValue} ${componentPrice.unitPrice.durationUnit}${
                    componentPrice.unitPrice.durationValue > 1 ? 's' : ''
                  }`}
                  {index === calculatedPriceResult.componentPrices.length - 1 && (
                    <>{` = ${displayMoneyFormat(calculatedPriceResult.totalPrice / 100, location?.currency)}`}</>
                  )}
                </Typography>
              );
            })}
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-[12px] justify-between mb-3">
        <div className="basis-[calc(50%_-_12px)]">
          {orderType === OrderType.Sale ? (
            <Field
              label="Price"
              isRequired
              type="currency"
              value={price}
              onChange={(v) => {
                if (Number.isNaN(v) && !price) {
                  setPrice(NaN);
                } else setPrice(v);
              }}
              maximumFractionDigits={2}
              dataCy="custom_field_price"
            />
          ) : (
            <></>
          )}
        </div>

        <div className="">
          <FieldLabel label="Quantity" />
          <QuantityInput name="Quantity" value={`${qty}`} onChange={(v) => setQty(parseInt(v ?? '1'))} />
        </div>
      </div>

      <Field
        type="dropdown"
        label="General Ledger Account"
        value={{
          label: generalLedgerAccount?.name,
          value: generalLedgerAccount?.id,
        }}
        dropdownProps={{ menuPortalTarget: document.body }}
        onChange={(v) => setGeneralLedgerAccount(accounts?.find((a) => a.id === v.value))}
        options={accounts?.map((a) => ({ label: a.name, value: a.id }))}
        dataCy="custom_field_gla"
      />

      {taxCategories && taxCategories.length > 0 && (
        <>
          <FieldLabel label="Tax Category" />
          <Field
            type="dropdown"
            value={{
              label: taxCategory?.name,
              value: taxCategory?.id,
            }}
            onChange={(a) => onTaxCategoryChange(a)}
            options={taxCategories?.map((a) => ({ label: a.name, value: a.id }))}
            isClearable={true}
          />
        </>
      )}

      <Checkbox
        label="Taxable"
        type="white"
        isChecked={taxable}
        onChange={(v) => setTaxable(v)}
        dataCy="custom_field_taxable"
      />

      <div className="button-group">
        <Button
          className="button-secondary"
          onClick={() => {
            onCollapse && onCollapse();
            clear();
          }}
          data-cy="cancel_add_product_to_order"
        >
          Cancel
        </Button>
        <Button className="button-primary" onClick={submit} disabled={isDisabled} data-cy="add_product_to_order">
          Add to order
        </Button>
      </div>
    </>
  );
};

const AvailabilityText = ({
  selectedQty,
  availability,
  complementMessage = '',
}: {
  selectedQty?: number;
  availability: number;
  complementMessage?: string;
}) => {
  return (
    <Typography
      type="text-sm"
      className={classNames({
        'text-quipli-black-500': availability,
        'text-warning-600': !availability || availability < (selectedQty || 1),
      })}
      dataCy="availability_text"
    >
      {availability > 0
        ? `There are ${availability} units available${complementMessage}.`
        : `No units are available during the selected time period${complementMessage}.`}
    </Typography>
  );
};

const ProductInventoryCalendar = ({
  productId,
  startDateTime,
  endDateTime,
  isCycleBilled,
  cycleLength,
}: {
  productId: number;
  startDateTime: DateTime;
  endDateTime?: DateTime;
  isCycleBilled: boolean;
  cycleLength: Duration;
}) => {
  const maxEndDate = startDateTime.plus(cycleLength);
  const end = endDateTime ? (isCycleBilled ? DateTime.min(endDateTime, maxEndDate) : endDateTime) : maxEndDate;
  const start = startDateTime.minus({ days: 2 });

  return (
    <div>
      <Divider />
      <div className="my-4">
        <InventoryGantt
          expandAll
          productIds={[productId]}
          startDateTime={start}
          duration={end.plus({ days: 2 }).diff(start, 'days')}
        />
      </div>
      <Divider />
    </div>
  );
};
